/**
 * Undo/redo — snapshot stacks over immutable Timelines.
 *
 * Because Timeline is immutable with structural sharing, a "snapshot" is just
 * a reference: pushing history is O(1) in time and near-O(1) in memory.
 * Undo/redo therefore maps 1:1 onto timeline states, which is exactly the
 * Fusion/Onshape semantic — undoing steps back through parametric operations
 * (add feature, edit params, reorder, rollback moves), not through pixels.
 */

import type { Timeline } from './timeline';

export interface HistoryEntry {
  readonly timeline: Timeline;
  /** Human label for the edit that PRODUCED this state, e.g. "Edit Extrude 2". */
  readonly label: string;
}

export class DocumentHistory {
  private undoStack: HistoryEntry[] = [];
  private redoStack: HistoryEntry[] = [];
  private current: HistoryEntry;
  /** Entries dropped off the bottom by the limit — keeps `depth` absolute. */
  private trimmed = 0;

  constructor(
    initial: Timeline,
    private readonly limit = 200,
  ) {
    this.current = { timeline: initial, label: 'New document' };
  }

  get timeline(): Timeline {
    return this.current.timeline;
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /** Labels for UI menus ("Undo Edit Extrude 2"). */
  get undoLabel(): string | undefined {
    return this.undoStack.length > 0 ? this.current.label : undefined;
  }

  get redoLabel(): string | undefined {
    return this.redoStack[this.redoStack.length - 1]?.label;
  }

  /**
   * Absolute checkpoint handle for rollback: the number of commits behind
   * the current state, counting entries the limit has trimmed away. Stays
   * valid across trims (unlike a bare stack length), so a token grabbed
   * before 200+ later commits still points at the right state.
   */
  get depth(): number {
    return this.trimmed + this.undoStack.length;
  }

  /**
   * Oldest depth a rollback can still reach — states below this were dropped
   * by the limit and are unrecoverable, so a checkpoint token under it must
   * error rather than silently land on the oldest survivor.
   */
  get oldestReachableDepth(): number {
    return this.trimmed;
  }

  /** Commit a new state produced by a timeline operation. Clears redo. */
  commit(timeline: Timeline, label: string): void {
    if (timeline === this.current.timeline) return; // no-op edit
    this.undoStack.push(this.current);
    if (this.undoStack.length > this.limit) {
      this.undoStack.shift();
      this.trimmed++; // keep `depth` an absolute commit index
    }
    this.current = { timeline, label };
    this.redoStack = [];
  }

  undo(): Timeline | undefined {
    const prev = this.undoStack.pop();
    if (!prev) return undefined;
    this.redoStack.push(this.current);
    this.current = prev;
    return prev.timeline;
  }

  redo(): Timeline | undefined {
    const next = this.redoStack.pop();
    if (!next) return undefined;
    this.undoStack.push(this.current);
    this.current = next;
    return next.timeline;
  }

  /** Drop redo-able states (dry-run trials must not be redo-reachable). */
  clearRedo(): void {
    this.redoStack = [];
  }
}
